import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from '../components/landing/Footer';
import { ChevronDown, Menu } from 'lucide-react';
import { HeroContent } from '../components/landing/HeroContent';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { useTranslation } from '../hooks/useTranslation';

const PrivacyPolicyPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [tocOpen, setTocOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { t } = useTranslation();

  // Register section refs
  useEffect(() => {
    // Force a reflow to ensure elements have proper dimensions
    setTimeout(() => {
      // Intentionally left empty - no logging needed
    }, 500);
  }, []);

  const toggleSection = (section: string) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  // Custom scroll function
  const scrollToSection = (sectionId: string, closeMenu: boolean = false) => {
    // Get the section ref
    const sectionRef = sectionRefs.current[sectionId];
    
    if (sectionRef) {
      // Get the position of the section
      const rect = sectionRef.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const sectionTop = rect.top + scrollTop;
      
      // Scroll to the section
      window.scrollTo({
        top: sectionTop - 100, // 100px offset from top
        behavior: 'smooth'
      });
      
      if (closeMenu) {
        setTocOpen(false);
      }
    }
  };

  const sections = [
    {
      id: 'information-collection',
      title: 'Information Collection',
      content: 'We collect minimal information necessary to provide our services. This may include information you provide when using our application, such as your email address, company information, and invoice data. We do not collect any information unless explicitly provided by you.'
    },
    {
      id: 'information-usage',
      title: 'Information Usage',
      content: 'We use the information solely to provide and improve our services. Your invoice data is used exclusively to generate and manage invoices as requested by you. We do not analyze, sell, or share your data with third parties for marketing purposes.'
    },
    {
      id: 'data-storage',
      title: 'Data Storage',
      content: 'Your data is stored securely and retained only for as long as necessary to provide our services or as required by law. You may request deletion of your data at any time by contacting us.'
    },
    {
      id: 'cookies',
      title: 'Cookies',
      content: 'Our application uses only essential cookies necessary for its functioning. We do not use tracking or advertising cookies. You can configure your browser to refuse cookies, but this may limit your ability to use some features of our application.'
    },
    {
      id: 'third-parties',
      title: 'Third-Party Services',
      content: 'We may use third-party services for hosting and infrastructure. These services are bound by their own privacy policies and may collect limited technical information as required for their operation. We do not share your personal data with these services beyond what is necessary for their functioning.'
    },
    {
      id: 'security',
      title: 'Security',
      content: 'We implement reasonable security measures to protect your data. However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.'
    },
    {
      id: 'changes',
      title: 'Changes to Privacy Policy',
      content: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes.'
    },
    {
      id: 'contact',
      title: 'Contact Us',
      content: 'If you have any questions about this Privacy Policy, please contact us through the contact information provided on our website.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* Privacy Policy Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              Privacy Policy
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              Last updated: May 29, 2025
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-16 md:flex-row md:px-8">
        {/* Table of Contents - Desktop */}
        <div 
          className="sticky left-0 top-20 hidden max-w-xs flex-col self-start pr-10 md:flex"
          onMouseLeave={() => setHoveredLink(null)}
        >
          {sections.map((section, index) => (
            <a
              className="group/toc-link relative rounded-lg px-2 py-1 text-sm text-neutral-700 dark:text-neutral-200 cursor-pointer"
              key={section.id}
              href={`#${section.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
              onMouseEnter={() => setHoveredLink(index)}
              onMouseLeave={() => setHoveredLink(null)}
            >
              {hoveredLink === index && (
                <motion.span
                  layoutId="toc-indicator"
                  className="absolute left-0 top-0 h-full w-1 rounded-br-full rounded-tr-full bg-neutral-200 dark:bg-neutral-700"
                />
              )}
              <span className="inline-block transition duration-200 group-hover/toc-link:translate-x-1">
                {section.title}
              </span>
            </a>
          ))}
        </div>

        {/* Mobile TOC Button */}
        <div className="sticky right-2 top-20 flex w-full flex-col items-end justify-end self-start md:hidden">
          <button
            onClick={() => setTocOpen(!tocOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm dark:bg-neutral-900"
          >
            <Menu className="h-6 w-6 text-black dark:text-white" />
          </button>
          <AnimatePresence>
            {tocOpen && (
              <motion.div
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                transition={{
                  duration: 0.2,
                }}
                className="mt-2 flex flex-col items-end rounded-3xl border border-neutral-100 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
              >
                {sections.map((section, index) => (
                  <a
                    className="group/toc-link relative rounded-lg px-2 py-1 text-right text-sm text-neutral-700 dark:text-neutral-200 cursor-pointer"
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(section.id, true);
                    }}
                    onMouseEnter={() => setHoveredLink(index)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    {hoveredLink === index && (
                      <motion.span
                        layoutId="toc-indicator-mobile"
                        className="absolute left-0 top-0 h-full w-1 rounded-br-full rounded-tr-full bg-neutral-200 dark:bg-neutral-700"
                      />
                    )}
                    <span className="inline-block transition duration-200 group-hover/toc-link:translate-x-1">
                      {section.title}
                    </span>
                  </a>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content */}
        <div className="flex max-w-2xl flex-1 flex-col">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="mb-6 p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-center">
              <p className="text-neutral-600 dark:text-neutral-300 text-sm font-medium">
                This content is available in English only
              </p>
            </div>
            <p className="text-neutral-600 dark:text-neutral-300">
              At InvoiceHappens, we prioritize your privacy. This policy outlines our practices regarding the collection, use, and disclosure of your information when you use our service. We only collect information that is necessary to provide and improve our service. By using our service, you agree to the collection and use of information in accordance with this policy.
            </p>

            {/* Mobile Accordion */}
            <div className="md:hidden mt-6 space-y-4">
              {sections.map((section) => (
                <div 
                  key={section.id} 
                  id={section.id}
                  ref={(el) => { sectionRefs.current[section.id] = el; }}
                  className="scroll-mt-24"
                >
                  <motion.div 
                    className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex w-full items-center justify-between p-4 text-left font-medium text-neutral-900 dark:text-white"
                    >
                      <span>{section.title}</span>
                      <ChevronDown 
                        className={`h-5 w-5 transition-transform duration-300 ${activeSection === section.id ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <AnimatePresence>
                      {activeSection === section.id && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 pt-0 text-neutral-600 dark:text-neutral-300">
                            <p>{section.content}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              ))}
            </div>

            {/* Desktop Content */}
            <div className="hidden md:block space-y-8 mt-6">
              {sections.map((section, index) => (
                <div 
                  key={section.id} 
                  id={section.id} 
                  ref={(el) => { sectionRefs.current[section.id] = el; }}
                  className="scroll-mt-24"
                >
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
                      {section.title}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-300">
                      {section.content}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="mt-10 max-w-2xl">
            <div className="h-px w-full bg-neutral-200 dark:bg-neutral-900" />
            <div className="h-px w-full bg-neutral-100 dark:bg-neutral-800" />
          </div>

          {/* Back to Top Button */}
          <motion.div 
            className="mt-10 text-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button 
              onClick={() => {
                try {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {
                  // Silent error handling
                }
              }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-all duration-300"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4 mr-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              {t('footer.backToTop')}
            </button>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage; 